import { useEffect, useState } from "react";
import { inventory } from "../../api/client";
import type { InventoryMeta } from "../../api/inventory-types";
import { Plus, Trash } from "../icons";
import { Field, type Tone } from "../parts";
import "./inventory.css";
import { t } from "../../i18n";

/*
 * What the inventory screens share: the inventory settings and locations (one request, kept for the session and refreshed when
 * Settings changes them), a lines editor for item / quantity / rate, and the status colours.
 */

export { GOODS_HIDDEN } from "../goods";
import { GOODS_HIDDEN } from "../goods";

let cache: InventoryMeta | null = null;
let pending: Promise<void> | null = null;
const listeners = new Set<(m: InventoryMeta | null) => void>();

/** Every screen reads inventory as off while goods are hidden, with only the default location, even if it was turned on. */
const shown = (m: InventoryMeta): InventoryMeta => (GOODS_HIDDEN ? { ...m, enabled: false, tracked_items: [], locations: m.locations.filter((l) => l.is_default).slice(0, 1) } : m);

/** Asks the server again (after inventory is turned on or a location is added) and tells every screen that uses it. */
export function refreshInventoryMeta() {
  pending = inventory
    .meta()
    .then((raw) => {
      const m = shown(raw);
      cache = m;
      listeners.forEach((fn) => fn(m));
    })
    .catch(() => undefined)
    .finally(() => {
      pending = null;
    });
  return pending;
}

/** Inventory on or off, its settings, the active locations and the tracked items. Null until the first answer. */
export function useInventoryMeta(): InventoryMeta | null {
  const [m, setM] = useState<InventoryMeta | null>(cache);
  useEffect(() => {
    listeners.add(setM);
    if (!cache && !pending) void refreshInventoryMeta();
    return () => {
      listeners.delete(setM);
    };
  }, []);
  return m;
}

export const errorText = (e: unknown, fallback = t("That did not work.")) => (e instanceof Error ? e.message : fallback);

export const STATUS_TONE: Record<string, Tone> = {
  draft: "neutral",
  adjusted: "green",
  open: "gold",
  in_transit: "amber",
  received: "green",
  delivered: "green",
  invoiced: "green",
  returned: "neutral",
  packed: "gold",
  shipped: "amber",
  void: "neutral",
};
export const statusText = (s: string) => s.replace("_", " ").replace(/^./, (c) => c.toUpperCase());

/* ---------- lines: item, quantity, rate ---------- */

export type LineDraft = { key: number; item_id: string; quantity: string; rate: string };
let seed = 1;
export const blankLine = (): LineDraft => ({ key: seed++, item_id: "", quantity: "1", rate: "" });

type Pickable = { id: string; name: string; unit: string | null; sku?: string | null };

/** Item, quantity and (optionally) a rate per line. qtyLabel and rateLabel name the columns; rateLabel null leaves the rate out. */
export function LinesEditor({ lines, onChange, items, qtyLabel = "Quantity", rateLabel = null, rateHint, signed = false }: { lines: LineDraft[]; onChange: (l: LineDraft[]) => void; items: Pickable[]; qtyLabel?: string; rateLabel?: string | null; rateHint?: string; signed?: boolean }) {
  const set = (key: number, p: Partial<LineDraft>) => onChange(lines.map((l) => (l.key === key ? { ...l, ...p } : l)));
  return (
    <div className="inv-lines" role="group" aria-label={t("Items")}>
      {lines.map((l, i) => {
        const it = items.find((x) => x.id === l.item_id);
        return (
          <div className={`inv-line${rateLabel ? " has-rate" : ""}`} key={l.key}>
            <Field label={t("Item {n}", { n: i + 1 })}>
              <select value={l.item_id} onChange={(e) => set(l.key, { item_id: e.target.value })}>
                <option value="">{t("Choose an item…")}</option>
                {items.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                    {x.sku ? ` · ${x.sku}` : ""}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={`${qtyLabel}${it?.unit ? ` (${it.unit})` : ""}`} hint={signed ? "Negative takes stock out." : undefined}>
              <input inputMode="decimal" value={l.quantity} onChange={(e) => set(l.key, { quantity: e.target.value })} />
            </Field>
            {rateLabel && (
              <Field label={rateLabel} hint={rateHint}>
                <input inputMode="decimal" value={l.rate} onChange={(e) => set(l.key, { rate: e.target.value })} placeholder="0.00" />
              </Field>
            )}
            <button type="button" className="icon-button inv-line__remove" aria-label={t("Remove item {n}", { n: i + 1 })} disabled={lines.length === 1} onClick={() => onChange(lines.filter((x) => x.key !== l.key))}>
              <Trash size={16} />
            </button>
          </div>
        );
      })}
      <button type="button" className="button button--secondary button--sm" style={{ justifySelf: "start" }} onClick={() => onChange([...lines, blankLine()])}>
        <Plus size={14} /> {t("Add a line")}
      </button>
    </div>
  );
}

const clean = (s: string) => s.replace(/[,\s₹]/g, "");
/** The filled-in lines, or an error to show. */
export function checkLines(lines: LineDraft[], { allowNegative = false, allowZero = false } = {}): string | null {
  const used = lines.filter((l) => l.item_id || l.quantity.trim() !== "1");
  if (!used.length || used.some((l) => !l.item_id)) return t("Choose an item on every line.");
  for (const l of used) {
    const q = Number(clean(l.quantity));
    if (!Number.isFinite(q) || (q === 0 && !allowZero) || (!allowNegative && q < 0)) return t("Every line needs a quantity.");
    if (l.rate.trim() && !/^\d+(\.\d{1,2})?$/.test(clean(l.rate))) return t("Rates are in rupees, with at most 2 decimals.");
  }
  return null;
}
export const qtyOf = (s: string) => clean(s);
export const rateOf = (s: string) => (s.trim() ? clean(s) : undefined);

/** A location picker; blank means the default location. */
export function LocationSelect({ meta, value, onChange, label = "Location", blank = "Default location" }: { meta: InventoryMeta | null; value: string; onChange: (v: string) => void; label?: string; blank?: string | null }) {
  return (
    <Field label={label}>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        {blank !== null && <option value="">{blank}</option>}
        {(meta?.locations ?? []).map((l) => (
          <option key={l.id} value={l.id}>
            {l.name}
            {l.is_default ? t(" (default)") : ""}
          </option>
        ))}
      </select>
    </Field>
  );
}
